import { useMutation, useQuery, useQueryClient } from 'react-query';
import { orderApi } from './api';
import type { CustomerAccountEmailPayload, OrderFilters, OrderFormValues } from './types';

const ORDER_LIST_KEY = 'orders:list';
const ORDER_DETAIL_KEY = 'orders:detail';
const ORDER_PACKAGES_KEY = 'orders:packages';
const ORDER_CUSTOMERS_KEY = 'orders:customers';

export const useOrderList = (filters: OrderFilters) => {
  return useQuery([ORDER_LIST_KEY, filters], () => orderApi.list(filters), {
    keepPreviousData: true,
    staleTime: 3 * 60 * 1000,
  });
};

export const useOrderDetail = (id?: string, enabled = false) => {
  return useQuery([ORDER_DETAIL_KEY, id], () => orderApi.get(id!), {
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useOrderPackages = () => {
  return useQuery([ORDER_PACKAGES_KEY], () => orderApi.getPackages(), {
    staleTime: 10 * 60 * 1000,
  });
};

export const useOrderCustomers = (search: string, enabled: boolean) => {
  return useQuery(
    [ORDER_CUSTOMERS_KEY, search],
    () => orderApi.getCustomers(search),
    {
      enabled: Boolean(enabled),
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: OrderFormValues) => orderApi.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORDER_LIST_KEY] });
      // Invalidate customers list vì có thể đã tạo customer mới
      queryClient.invalidateQueries({ queryKey: ['customers:list'] });
    },
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: { id: string; payload: Pick<OrderFormValues, 'order_items' | 'notes'> }) =>
      orderApi.update(id, payload),
    {
      onSuccess: (record) => {
        queryClient.invalidateQueries({ queryKey: [ORDER_LIST_KEY] });
        queryClient.setQueryData([ORDER_DETAIL_KEY, record.id], record);
      },
    }
  );
};

export const useDeleteOrder = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => orderApi.delete(id), {
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [ORDER_LIST_KEY] });
      queryClient.removeQueries([ORDER_DETAIL_KEY, id]);
    },
  });
};

export const useMarkOrderPaid = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => orderApi.markAsPaid(id), {
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: [ORDER_LIST_KEY] });
      queryClient.setQueryData([ORDER_DETAIL_KEY, record.id], record);
      // Invalidate customers list vì customer status đã được cập nhật thành 'active'
      queryClient.invalidateQueries({ queryKey: ['customers:list'] });
      queryClient.invalidateQueries({ queryKey: ['customers:summary'] });
    },
  });
};

export const useSendCustomerAccountEmail = () => {
  return useMutation((payload: CustomerAccountEmailPayload) => orderApi.sendCustomerAccountEmail(payload));
};

export const useTodayOrders = () => {
  return useQuery(['orders:today'], () => orderApi.getTodayOrders(), {
    staleTime: 2 * 60 * 1000, // 2 phút
    refetchOnWindowFocus: false,
  });
};

export const usePendingOrders = () => {
  return useQuery(['orders:pending'], () => orderApi.getPendingOrders(), {
    staleTime: 2 * 60 * 1000, // 2 phút
    refetchOnWindowFocus: false,
  });
};

export const useTodayRevenue = () => {
  return useQuery(['orders:today-revenue'], () => orderApi.getTodayRevenue(), {
    staleTime: 2 * 60 * 1000, // 2 phút
    refetchOnWindowFocus: false,
  });
};

export const useOrdersStatistics = (dateFrom?: string, dateTo?: string) => {
  return useQuery(
    ['orders:statistics', dateFrom, dateTo],
    () => orderApi.getOrdersStatistics(dateFrom, dateTo),
    {
      staleTime: 5 * 60 * 1000, // 5 phút
      refetchOnWindowFocus: false,
    }
  );
};


